/**
 * DASHBOARD TAB URLS
 *
 * The consultant dashboard's tabs each live at a URL under the dashboard's
 * real route (`/dashboard/:tab?` in routes.tsx), so a tab survives reload and
 * back/forward. Each tab belongs to one of the firm's three workspaces (Sell,
 * Deliver, Firm), so the URL also says which workspace is open: there is one
 * source of truth, the tab. Import-free; `tests/unit/dashboardTabs.test.ts`
 * and `tests/unit/workspaces.test.ts` pin it.
 *
 * The old `/consultant/...` tab paths never had a route: the router's
 * catch-all sent them back to `/dashboard`, which remounted the dashboard on
 * the Clients tab (the "flash" when clicking any other tab).
 */

export const DASHBOARD_PATH = '/dashboard';

export type DashboardTab =
  // Deliver
  | 'overview'
  | 'review-queue'
  | 'deliver'
  | 'wizards'
  | 'analytics'
  | 'demo'
  // Sell
  | 'proposals'
  | 'sows'
  | 'sizing'
  | 'catalog'
  // Firm
  | 'portfolio'
  | 'team'
  | 'settings'
  | 'billing'
  | 'licenses'
  | 'audit';

export type DashboardWorkspace = 'sell' | 'deliver' | 'firm';

/** Tab id → URL segment. The overview tab is the bare dashboard URL. */
export const DASHBOARD_TAB_SLUGS: Record<DashboardTab, string> = {
  overview: '',
  'review-queue': 'queue',
  deliver: 'deliver',
  wizards: 'wizards',
  analytics: 'analytics',
  demo: 'demo',
  proposals: 'proposals',
  sows: 'sows',
  sizing: 'sizing',
  catalog: 'catalog',
  portfolio: 'portfolio',
  team: 'team',
  settings: 'settings',
  billing: 'billing',
  licenses: 'licenses',
  audit: 'audit',
};

/**
 * The primary items of each workspace, in order. The rule is five to seven
 * per workspace (SIMPLIFICATION_PLAN.md §1); `demo` shows only on demo hosts.
 * The first entry is where a workspace opens.
 */
export const WORKSPACE_TABS: Record<DashboardWorkspace, readonly DashboardTab[]> = {
  deliver: ['overview', 'review-queue', 'deliver', 'wizards', 'analytics', 'demo'],
  sell: ['proposals', 'sows', 'sizing', 'catalog'],
  firm: ['portfolio', 'team', 'settings', 'billing', 'licenses', 'audit'],
};

export const MAX_PRIMARY_ITEMS = 7;

/** Old slugs that still arrive in links and bookmarks, and the tab they mean now. */
export const LEGACY_TAB_SLUGS: Record<string, DashboardTab> = {
  bd: 'catalog',
  questions: 'settings',
  'action-items': 'review-queue',
};

const SLUG_TO_TAB: Record<string, DashboardTab> = Object.fromEntries(
  Object.entries(DASHBOARD_TAB_SLUGS)
    .filter(([, slug]) => slug !== '')
    .map(([tab, slug]) => [slug, tab as DashboardTab]),
);

const TAB_TO_WORKSPACE: Record<string, DashboardWorkspace> = Object.fromEntries(
  (Object.keys(WORKSPACE_TABS) as DashboardWorkspace[]).flatMap((ws) => WORKSPACE_TABS[ws].map((tab) => [tab, ws])),
);

export function isDashboardTab(value: string): value is DashboardTab {
  return Object.prototype.hasOwnProperty.call(DASHBOARD_TAB_SLUGS, value);
}

/** The workspace a tab belongs to; an unknown tab is on Deliver, the home. */
export function workspaceForTab(tab: string): DashboardWorkspace {
  return TAB_TO_WORKSPACE[tab] ?? 'deliver';
}

/** The tab a workspace opens on. */
export function firstTabOf(workspace: DashboardWorkspace): DashboardTab {
  return WORKSPACE_TABS[workspace][0];
}

/** The URL for a tab: `/dashboard` for the overview, `/dashboard/<slug>` otherwise. */
export function dashboardTabPath(tab: string): string {
  if (!isDashboardTab(tab)) return DASHBOARD_PATH;
  const slug = DASHBOARD_TAB_SLUGS[tab];
  return slug ? `${DASHBOARD_PATH}/${slug}` : DASHBOARD_PATH;
}

/** The tab for a URL segment; a missing or unknown segment is the overview, an old one its new home. */
export function dashboardTabFromSlug(slug: string | undefined | null): DashboardTab {
  if (!slug) return 'overview';
  return SLUG_TO_TAB[slug] ?? LEGACY_TAB_SLUGS[slug] ?? 'overview';
}

/** The tab for a full pathname such as `/dashboard/queue/` or `/dashboard`. */
export function dashboardTabFromPath(pathname: string): DashboardTab {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'dashboard') return 'overview';
  return dashboardTabFromSlug(parts[1]);
}
